import { Box } from "../../box";
import { useButton } from "./useButton";
import { forwardRef, useMemo } from "react";
import { as, createSizeAdapter, cssModule, mergeProps, omitProps, slot, useSlots } from "../../shared";
import { useResponsiveValue, useStyleProps } from "../../styling";
import { Text } from "../../typography";
import { embeddedIconSize } from "../../icons";
import { useFormButton } from "../../form";
import { useInputGroupButtonAddonProps } from "../../input-group";
import { useToolbarProps } from "../../toolbar";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const DefaultElement = "button";

/* eslint-disable sort-keys, sort-keys-fix/sort-keys-fix */
const condensedTextSize = createSizeAdapter({
  "sm": "md",
  "md": "lg"
});
/* eslint-enable sort-keys, sort-keys-fix/sort-keys-fix */

export function InnerButton(props) {
  const [formProps] = useFormButton();
  const [toolbarProps] = useToolbarProps();
  const [inputGroupProps] = useInputGroupButtonAddonProps();
  const [styleProps] = useStyleProps("button");
  const {
    active,
    as: asProp = DefaultElement,
    autoFocus,
    children,
    condensed,
    disabled,
    fluid,
    focus,
    forwardedRef,
    hover,
    loading,
    shape,
    size,
    type,
    variant = "secondary",
    inherit,
    ...rest
  } = mergeProps(props, formProps, omitProps(toolbarProps, ["orientation"]), inputGroupProps, styleProps);
  const fluidValue = useResponsiveValue(fluid);
  const sizeValue = useResponsiveValue(size);
  const {
    ref: buttonRef,
    ...buttonProps
  } = useButton({
    active,
    as: asProp,
    autoFocus,
    cssModule: "o-ui-text-button",
    fluid: fluidValue,
    focus,
    forwardedRef,
    hover,
    inherit,
    loading,
    shape,
    size: sizeValue,
    type,
    variant
  });
  const {
    counter,
    "end-icon": endIcon,
    icon,
    text
  } = useSlots(children, useMemo(() => ({
    _: {
      defaultWrapper: Text
    },
    counter: {
      className: "o-ui-button-counter",
      color: "inherit",
      disabled,
      pushed: true,
      size: condensed ? condensedTextSize(sizeValue) : sizeValue
    },
    "end-icon": {
      className: "o-ui-button-end-icon",
      size: condensed ? sizeValue : embeddedIconSize(sizeValue)
    },
    icon: {
      className: "o-ui-button-icon o-ui-button-start-icon",
      size: condensed ? sizeValue : embeddedIconSize(sizeValue)
    },
    text: {
      "aria-hidden": loading,
      className: "o-ui-button-text",
      size: condensed ? condensedTextSize(sizeValue) : sizeValue
    }
  }), [sizeValue, disabled, condensed, loading]));
  return /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
      as: asProp,
      className: cssModule("o-ui-button", icon && "has-start-icon", endIcon && "has-end-icon"),
      disabled,
      ref: buttonRef
    }, buttonProps),
    children: [icon, text, counter, endIcon]
  });
}
InnerButton.defaultElement = DefaultElement;
export const Button = slot("button", /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerButton, { ...props,
  forwardedRef: ref
})));
///////////
export const ButtonAsLink = as(Button, "a");